import {Component, inject, OnInit, DestroyRef} from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import {GalaxyNavControlService} from "@vendasta/galaxy/nav";
import {MatCardModule} from "@angular/material/card";
import {GalaxyFormFieldModule} from "@vendasta/galaxy/form-field";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {NgForOf} from "@angular/common";
import {MatInput} from "@angular/material/input";
import { signal } from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {GalaxyStickyFooterModule} from "@vendasta/galaxy/sticky-footer";
import {GalaxyButtonLoadingIndicatorModule} from "@vendasta/galaxy/button-loading-indicator";
import {MatButton} from "@angular/material/button";
import {Router} from '@angular/router';
import {
  AiAssistantService, ASSISTANT_ID_REPUTATION_SPECIALIST,
  DEFAULT_AVATAR_SVG_ICON
} from "@galaxy/ai-assistant";
import {GalaxyAvatarModule} from "@vendasta/galaxy/avatar";
import {PageService} from "@vendasta/galaxy/page/src/page.service";
import {toSignal, takeUntilDestroyed} from "@angular/core/rxjs-interop";
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {GalaxyLoadingSpinnerModule} from "@vendasta/galaxy/loading-spinner";
import {
  AutoReviewResponseConfigurationApiService
} from "@vendasta/reputation";
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {TranslateModule} from "@ngx-translate/core";
import {AssistantInterface} from "@vendasta/ai-assistants";
import { REPUTATION_CONFIG_TOKEN } from '../tokens';

@Component({
  selector: 'lib-auto-review-response-settings',
  imports: [
    GalaxyPageModule,
    MatCardModule,
    GalaxyFormFieldModule,
    MatOption,
    MatSelect,
    MatCheckboxModule,
    NgForOf,
    MatInput,
    GalaxyStickyFooterModule,
    GalaxyButtonLoadingIndicatorModule,
    MatButton,
    GalaxyAvatarModule,
    ReactiveFormsModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule
  ],
  templateUrl: './auto-review-response-settings.component.html',
  styleUrl: './auto-review-response-settings.component.scss'
})
export class AutoReviewResponseSettingsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly reputationConfig = inject(REPUTATION_CONFIG_TOKEN);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private fb =  inject(FormBuilder);
  private readonly bizNavControl = this.reputationConfig.isBusinessApp
    ? inject(GalaxyNavControlService)
    : null;
  protected readonly isLoading = signal(false);
  protected readonly isFormDirty = signal(false);
  private readonly initialFormValue = signal<any>({});
  protected readonly settingsForm: FormGroup = this.fb.group({
    rating: [null],
    sources: [[]],
    delayValue: [''],
    delayUnit: [''],
  });
  assetPath = (basePath: string) =>
    window.location.hostname === 'localhost'
      ? `/assets/reputation/${basePath}`
      : `https://vstatic-prod.apigateway.co/business-center-client.9223372035086373284.prod/assets/ai-assistant/avatars/${basePath}`;
  private readonly accountGroupId = toSignal(this.reputationConfig.accountGroupId$);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly partnerId = toSignal(this.reputationConfig.partnerId$);
  private readonly router = inject(Router);
  private readonly pageService = inject(PageService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly autoReviewResponseService = inject(AutoReviewResponseConfigurationApiService);
  protected readonly reputationAssistant = signal<AssistantInterface | null>(null);
  protected readonly assistantName = signal<string>('');
  public previousPageUrl = "";
  public minLimit = 1;
  public maxLimit= 60;

  starOptions = [
    { value: 5, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.RATING_OPTIONS.5_STARS' },
    { value: 4, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.RATING_OPTIONS.4_STARS_ABOVE' },
    { value: 3, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.RATING_OPTIONS.3_STARS_ABOVE' },
    { value: 2, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.RATING_OPTIONS.2_STARS_ABOVE' },
    { value: 1, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.RATING_OPTIONS.1_STAR_ABOVE' },
  ];

  delayOptionsInPeriod = [
    { value: 'minutes', label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.DELAY_UNITS.MINUTES' },
    { value: 'hours', label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.DELAY_UNITS.HOURS' },
  ];

  availableSources = [
    { key: 10010, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.PLATFORMS.GOOGLE' },
    { key: 10050, label: 'PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.PLATFORMS.FACEBOOK' }
  ];

  ngOnInit(){
    this.bizNavControl?.closeForThisPage();
    this.loadReputationAssistant();
    this.getAutoResponseConfiguration();
    // Track form changes to enable/disable Save button
    this.settingsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const current = this.settingsForm.getRawValue();
        const initial = { ...this.initialFormValue() }
        if (current.delayUnit === 'hours') {
          this.maxLimit = 6;
        } else {
          this.maxLimit = 60;
        }
        const currDelayValue = current.delayValue;
        const currDelayUnit = current.delayUnit;
        current.sources = current.sources?[...current.sources].sort() : [];
        initial.sources = initial.sources?[...initial.sources].sort() : [];
        const hasChanges =
          JSON.stringify(current) !== JSON.stringify(initial);
        if (currDelayUnit === "minutes" && (currDelayValue<=0 || currDelayValue>60)) {
          this.isFormDirty.set(false);
        }else if(currDelayUnit === "hours" && (currDelayValue<=0 || currDelayValue>6)) {
          this.isFormDirty.set(false);
        }else{
          this.isFormDirty.set(hasChanges);
        }
      });
    if(this.reputationConfig.isBusinessApp) {
      this.previousPageUrl =
        this.pageService.getPreviousPageUrl('/account/location/'+this.accountGroupId()+'/ai/assistants/ASSISTANT-reputation-specialist/edit');
    }
  }

  onSave(): void {
    this.analyticsService.trackEvent(
      'ai-assistant-reputation-specialist-auto-response-save-clicked',
      'reputation',
      'click',
      undefined,
      {
        assistantId: ASSISTANT_ID_REPUTATION_SPECIALIST,
        accountGroupId: this.accountGroupId(),
      },
    );

    // Check rating
    const ratingControl = this.settingsForm.get('rating');
    if (!ratingControl?.value || ratingControl?.value.length === 0) {
      ratingControl?.markAsTouched();
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.VALID_RATING");
      return; // stop further validation
    }

    // Check sources
    const sourcesControl = this.settingsForm.get('sources');
    if (!sourcesControl?.value || sourcesControl.value.length === 0) {
      sourcesControl?.markAsTouched();
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.VALID_SOURCES");
      return; // stop further validation
    }

    // Check delay value
    const delayValueControl = this.settingsForm.get('delayValue');
    // Check delay unit
    const delayUnitControl = this.settingsForm.get('delayUnit');

    if (!delayValueControl?.value || delayValueControl.value <= 0) {
      delayValueControl?.markAsTouched();
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.VALID_DELAY_VALUE");
      return;
    } else if(delayValueControl?.value > 60 && delayUnitControl?.value === "minutes") {
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.INVALID_MINUTES_VALUE")
      return;
    } else if(delayValueControl?.value > 6 && delayUnitControl?.value === "hours") {
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.INVALID_HOURS_VALUE");
      return;
    }

    if (!delayUnitControl?.value) {
      delayUnitControl?.markAsTouched();
      this.snackbarService.errorSnack("PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.VALID_DELAY_UNIT");
      return;
    }

    this.isLoading.set(true);

    const formData = this.settingsForm.value;
    const selectedRatings = this.getSelectedRatings();
    const selectedSources = this.getSelectedSourceIds();
    const delayInMinutes = this.convertDelayToMinutes(
      formData.delayValue,
      formData.delayUnit
    );

    this.isLoading.set(false);
      this.autoReviewResponseService
        .upsertAutoReviewResponseConfiguration({
          accountGroupId: this.accountGroupId() || '',
          autoReviewResponseSettings: {
            sources: selectedSources,
            supportedStarRating: selectedRatings,
            responseDelayMinutes: delayInMinutes,
          }
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.snackbarService.successSnack('PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.SUCCESS.SAVING_SETTINGS_SUCCESS');
            this.isLoading.set(false);
            // Reset baseline after successful save
            this.initialFormValue.set(this.settingsForm.getRawValue());
            this.isFormDirty.set(false);
          },
          error: () => {
            this.snackbarService.errorSnack('PERFORMANCE.REPUTATION.AUTO_REVIEW_RESPONSE.SETTINGS.ERRORS.ERROR_SAVING_SETTINGS');
            this.isLoading.set(false);
          }
        })
  }

  protected goToConfigPage(): void {
    let previousUrl = '/account/location/' + this.accountGroupId() + '/ai/assistants/ASSISTANT-reputation-specialist/edit';

    if (this.reputationConfig.isBusinessApp) {
      previousUrl = this.pageService.getPreviousPageUrl(previousUrl);
    }

    this.pageService.pop(); // remove current page from history to prevent loop
    this.router.navigateByUrl(previousUrl);
  }

  protected readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;

  private loadReputationAssistant(): void {
    const partnerId = this.partnerId();
    const accountGroupId = this.accountGroupId();

    if (partnerId && accountGroupId) {
      this.isLoading.set(true);

      // Get the reputation specialist assistant
      this.aiAssistantService
        .getAssistant(partnerId, accountGroupId, ASSISTANT_ID_REPUTATION_SPECIALIST)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (assistant) => {
            this.reputationAssistant.set(assistant);
            this.assistantName.set(assistant.name || 'Reputation Specialist');
            this.isLoading.set(false);
          },
          error: () => {
            // Fallback to default name
            this.assistantName.set('Reputation Specialist');
            this.isLoading.set(false);
          }
        })
    } else {
      // Fallback if no IDs available
      this.assistantName.set('Reputation Specialist');
    }
  }

  private getAutoResponseConfiguration(): void {
    this.isLoading.set(true);
    this.autoReviewResponseService
      .getAutoReviewResponseConfiguration({
        accountGroupId: this.accountGroupId() || '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);

          if (response && response.autoReviewResponseSettings) {
            const settings = response.autoReviewResponseSettings;

            // Patch rating
            if (settings.supportedStarRating && settings.supportedStarRating.length) {
              // Assuming your getRatingArray maps to min rating for your select
              this.settingsForm.patchValue({
                rating: Math.min(...settings.supportedStarRating),
              });
            }

            // Patch sources
            if (settings.sources && settings.sources.length) {
              const sourceKeys = settings.sources.map((sourceId) => {
                const found = this.availableSources.find(s => s.key === sourceId);
                return found?.key;
              }).filter(Boolean);

              this.settingsForm.patchValue({ sources: sourceKeys });
            }

            // Patch delay
            if (settings.responseDelayMinutes !== undefined && settings.responseDelayMinutes > 0) {
              const delay = settings.responseDelayMinutes;
              if(delay>0 && delay<=60){
                this.settingsForm.patchValue({
                  delayValue: delay,
                  delayUnit: 'minutes',
                });
              }else{
                const delayInHours = delay/60;
                this.settingsForm.patchValue({
                  delayValue: delayInHours,
                  delayUnit: 'hours',
                });
              }
            }
          }
          // else: leave form as is (empty placeholders)
          this.initialFormValue.set(this.settingsForm.getRawValue());
          this.isFormDirty.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          // Form remains empty
        }
      });
  }


  // Method to get selected ratings for display or processing
  private getSelectedRatings(): number[] {
    const threshold = this.settingsForm.get('rating')?.value;
    return this.getRatingArray(threshold);
  }

  // Method to get the actual rating array based on selection
  private getRatingArray(threshold: number): number[] {
    if (!threshold) return [];
    const ratings: number[] = [];
    for (let i = threshold; i <= 5; i++) {
      ratings.push(i);
    }
    return ratings;
  }

  // Check if a source is selected
  public isSourceSelected(sourceKey: number): boolean {
    const selectedSources = this.settingsForm.get('sources')?.value || [];
    return selectedSources.includes(sourceKey);
  }

// Toggle a source selection
  public toggleSource(sourceKey: number): void {
    const sourcesControl = this.settingsForm.get('sources');
    const currentSources: number[] = sourcesControl?.value || [];

    let updatedSources: number[];

    if (currentSources.includes(sourceKey)) {
      // Remove the source
      updatedSources = currentSources.filter(source => source !== sourceKey);
    } else {
      // Add the source
      updatedSources = [...currentSources, sourceKey];
    }

    sourcesControl?.setValue(updatedSources);
  }

  // Get selected source IDs (for API or mapping)
  private getSelectedSourceIds(): number[] {
    const selectedSources: number[] = this.settingsForm.get('sources')?.value || [];
    return this.availableSources
      .filter(source => selectedSources.includes(source.key))
      .map(source => source.key);
  }

  // Method to convert delay to minutes
  private convertDelayToMinutes(value: number, unit: string): number {
    if (!value || value <= 0) return 0;

    switch (unit) {
      case 'minutes':
        return value; // Already in minutes
      case 'hours':
        return value * 60; // Convert hours to minutes
      default:
        return value; // Default to minutes if unit is unknown
    }
  }
}
